import { Box, Flex, Stack } from '@mantine/core';
import type { JSX } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Drawer, Label, Pill, Radios } from '@feathr/components';
import { cssVar, SSETransport, useRealtimeContext } from '@feathr/hooks';

import type { IDonationCreatedEvent } from '../../App/FundraisePage/hooks';
import { useFundraisingEvents } from '../../App/FundraisePage/hooks';
import type { TDebuggerMode } from './DonationEventForm';
import DonationEventForm from './DonationEventForm';

import * as styles from './RealtimeDebugger.css';

type TConnectionStatus = 'connected' | 'disconnected';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
}

function RealtimeDebugger({ isOpen, onClose }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { transport } = useRealtimeContext();
  const [connectionStatus, setConnectionStatus] = useState<TConnectionStatus>(
    transport.isConnected ? 'connected' : 'disconnected',
  );

  const isSSETransport = transport instanceof SSETransport;

  /*
   * Poll connection status since ITransport doesn't expose connection change events.
   * Only poll when the debugger is open to avoid unnecessary overhead.
   */
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const checkConnection = (): void => {
      setConnectionStatus(transport.isConnected ? 'connected' : 'disconnected');
    };

    const intervalId = setInterval(checkConnection, 1000);
    return (): void => clearInterval(intervalId);
  }, [isOpen, transport]);

  const modeOptions = [
    { id: 'mock' as const, name: t('Mock (local events)') },
    {
      id: 'integration' as const,
      name: t('Integration (backend)'),
      disabled: !isSSETransport,
    },
  ];
  const [mode, setMode] = useState<TDebuggerMode>('mock');
  const { publishDonationCreated } = useFundraisingEvents();

  const handleModeChange = useCallback((value?: string): void => {
    if (value) {
      setMode(value as TDebuggerMode);
    }
  }, []);

  const handleSummonDonation = useCallback(
    (donation: IDonationCreatedEvent): void => {
      publishDonationCreated(donation);
    },
    [publishDonationCreated],
  );

  return (
    <Drawer
      isOpen={isOpen}
      name={'realtime-debugger'}
      onClose={onClose}
      title={t('Realtime debugger')}
      width={400}
    >
      <Drawer.Content addVerticalGap={true}>
        <Stack className={styles.root} gap={cssVar('--spacing-2')}>
          <Flex align={'baseline'} gap={cssVar('--spacing-2')}>
            <Label>{t('Connection')}</Label>
            <Pill
              indicatorVariant={'solid'}
              theme={connectionStatus === 'connected' ? 'green' : 'neutral'}
            >
              {connectionStatus === 'connected' ? t('Connected') : t('Disconnected')}
            </Pill>
          </Flex>

          <Radios
            label={t('Mode')}
            onChange={handleModeChange}
            options={modeOptions}
            value={mode}
          />

          <Box>
            <Label>{t('Events')}</Label>
            <DonationEventForm mode={mode} onSummon={handleSummonDonation} />
          </Box>
        </Stack>
      </Drawer.Content>
    </Drawer>
  );
}

export default RealtimeDebugger;
